import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QRCodePage = () => {
  const { poolId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [pool, setPool] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [publicUrl, setPublicUrl] = useState('');

  useEffect(() => {
    fetchPoolAndQR();
  }, [poolId]);

  const fetchPoolAndQR = async () => {
    try {
      const poolRes = await axios.get(`${API}/pools/${poolId}`);
      setPool(poolRes.data);
      
      // Fetch QR code image as blob with authentication
      const qrResponse = await axios.get(`${API}/qr/${poolId}`, {
        responseType: 'blob'
      });
      
      // Create object URL from blob
      const qrBlobUrl = URL.createObjectURL(qrResponse.data);
      setQrCodeUrl(qrBlobUrl);
      
      // Public view URL
      const publicViewUrl = `${window.location.origin}/public/${poolId}`;
      setPublicUrl(publicViewUrl);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQR = async () => {
    try {
      // If qrCodeUrl is already a blob URL, fetch it directly
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `qr_code_${pool?.pool_name?.replace(/\s+/g, '_')}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('QR Code downloaded');
    } catch (error) {
      console.error('Failed to download QR:', error);
      toast.error(t('error'));
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success('URL copied to clipboard');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="qr-code-page">
      <div className="flex items-center space-x-4">
        <Button
          data-testid="back-to-pools-button"
          onClick={() => navigate('/pools')}
          variant="outline"
          size="sm"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-4xl font-bold text-secondary tracking-tight">{t('qrCode')}</h1>
          <p className="text-muted-foreground mt-1">{pool?.pool_name}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>QR Code</CardTitle>
            <CardDescription>
              Scan this QR code to view 30-day compliance history
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-center p-8 bg-white rounded-lg border-2 border-border">
              <img
                src={qrCodeUrl}
                alt="QR Code"
                data-testid="qr-code-image"
                className="max-w-full h-auto"
                style={{ width: '300px', height: '300px' }}
              />
            </div>
            <Button
              data-testid="download-qr-button"
              onClick={handleDownloadQR}
              className="w-full"
            >
              Download QR Code
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Public View URL</CardTitle>
            <CardDescription>
              Share this link for public access to compliance data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Public URL</label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  data-testid="public-url-input"
                  value={publicUrl}
                  readOnly
                  className="flex-1 px-3 py-2 border rounded-md bg-accent text-sm font-mono"
                />
                <Button
                  data-testid="copy-url-button"
                  onClick={handleCopyUrl}
                  variant="outline"
                >
                  Copy
                </Button>
              </div>
            </div>

            <div className="bg-accent p-4 rounded-lg space-y-2">
              <h3 className="font-medium text-sm">Pool Information</h3>
              <div className="space-y-1 text-sm">
                <p><span className="font-medium">Property:</span> {pool?.property_name}</p>
                <p><span className="font-medium">Pool:</span> {pool?.pool_name}</p>
                <p><span className="font-medium">Address:</span> {pool?.address}</p>
                <p><span className="font-medium">Volume:</span> {pool?.volume}</p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <h3 className="font-medium text-sm text-blue-900 mb-2">Usage Instructions</h3>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>Print and display the QR code at your facility</li>
                <li>Visitors can scan to view compliance history</li>
                <li>Data shows last 30 days of readings</li>
                <li>No login required for public access</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QRCodePage;